import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Contact } from '../components/Contact';
import { BUSINESS_FACTS, PRICING_TABLE, EXTRAS_TABLE, FAQ_CATEGORIES, ALL_FAQS, CITY_PAGE_AREAS } from '../data/knowledgeBase';
import { businessEntity, websiteEntity, injectJsonLd, setHeadMeta, canonicalUrl, BUSINESS_ID, WEBSITE_ID, SITE_URL } from '../utils/seo';
const PATH = '/faq';
const TITLE = "Mobile Car Detailing FAQ & Pricing in Ventura County | Mike's Pro Detail";
const DESCRIPTION = "Answers to common questions about mobile car detailing from Mike's Pro Detail — pricing, what's included, service areas, scheduling, and payment. Call (818) 381-9905.";
/**
 * /faq — the customer information page.
 *
 * A single, plain-text, crawlable page carrying the official answer to every
 * common customer question, plus the pricing table and business facts. Built
 * entirely from the shared knowledge base so nothing here can contradict the
 * rest of the site. Designed to match the existing site exactly.
 */
export function FaqPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setHeadMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      ogType: 'article'
    });
    const url = canonicalUrl(PATH);
    return injectJsonLd([businessEntity, websiteEntity, {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      url,
      name: 'Mobile Car Detailing FAQ',
      description: DESCRIPTION,
      isPartOf: {
        '@id': WEBSITE_ID
      },
      about: {
        '@id': BUSINESS_ID
      },
      mainEntity: ALL_FAQS.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    }, {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: url
      }]
    }]);
  }, []);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-800" data-mp="1juocxnngrwfl">
      <Navigation data-id="element-801" data-mp="1otivcohiriz6" />

      {/* Header */}
      <header className="w-full bg-black pt-32 pb-16 px-4 border-b border-zinc-900" data-id="element-802" data-mp="1oshg07th0abn">
        <div className="max-w-4xl mx-auto" data-id="element-803" data-mp="1a3toqdqnqmri">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-804" data-mp="1a31tj40z2r1r">
            <ol className="flex items-center gap-2 text-sm text-gray-500" data-id="element-805" data-mp="1ewmriw2kfl0y">
              <li data-id="element-806" data-mp="114q435budzaw">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-807" data-mp="1159cnvflrbc9">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" className="text-zinc-600" data-id="element-808" data-mp="1616q9tzhe3lc">
                /
              </li>
              <li className="text-gray-300" aria-current="page" data-id="element-809" data-mp="r8d1fhybkb40">
                FAQ
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black uppercase mb-4" data-id="element-810" data-mp="cis92672tzg1">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-811" data-mp="8hzwp42wrefj">
              Mobile Detailing FAQ &amp; Customer Information
            </span>
          </h1>
          <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-812" data-mp="hi5258ojk2r2" />
          <p className="text-gray-400 text-lg mt-6 max-w-3xl leading-relaxed" data-id="element-813" data-mp="vhe71c7arafq">
            Everything you need to know about mobile car detailing with Mike's
            Pro Detail — pricing, what each package includes, the areas we
            serve, how to book, and what we need from you. If your question
            isn't answered here, call or text{' '}
            <a href="tel:8183819905" className="text-red-500 hover:underline" data-id="element-814" data-mp="10b8rihuq7tfv">
              (818) 381-9905
            </a>
            .
          </p>
        </div>
      </header>

      {/* Business at a glance */}
      <section className="w-full bg-black py-12 px-4" aria-labelledby="business-facts" data-id="element-815" data-mp="33q2jszr0z7e4">
        <div className="max-w-4xl mx-auto" data-id="element-816" data-mp="38ow0f11lzeii">
          <h2 id="business-facts" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-817" data-mp="38nueg1j217m0">
            <span className="text-red-600" data-id="element-818" data-mp="3dgx787g0cd15">Business</span> at a Glance
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-819" data-mp="3idnmisjzn4li">
            {BUSINESS_FACTS.map(fact => <div key={fact.label} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-id="element-820" data-mp="2krqczknl69lp">
                <dt className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-821" data-mp="2pr2wbvf90yjj">
                  {fact.label}
                </dt>
                <dd className="text-gray-200 leading-relaxed" data-id="element-822" data-mp="2ur7bni4f7n86">{fact.value}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Pricing */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="faq-pricing" data-id="element-823" data-mp="9y3ws3ij04b7">
        <div className="max-w-4xl mx-auto" data-id="element-824" data-mp="ewf8ykwamqwl">
          <h2 id="faq-pricing" className="text-2xl md:text-3xl font-black uppercase text-white mb-2" data-id="element-825" data-mp="1zfre3d0cenus">
            <span className="text-red-600" data-id="element-826" data-mp="1uh6e9ep0666v">Packages</span> &amp; Pricing
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-827" data-mp="1pq7u240w8nyc">
            Every package is priced in two tiers: SM/MID for sedans and smaller
            vehicles, and SUV/CROSS for SUVs and crossovers. Prices range from
            $90 to $350.
          </p>

          <div className="space-y-4" data-id="element-828" data-mp="1ftlbjlw1i8zh">
            {PRICING_TABLE.map(pkg => <div key={pkg.name} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-829" data-mp="1bpwzapwgqp7w">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3" data-id="element-830" data-mp="16qk49hvfubu8">
                  <h3 className="text-xl font-black uppercase text-white" data-id="element-831" data-mp="11ssd4q2matr3">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-300 font-bold" data-id="element-832" data-mp="3141whi6sfwvl">
                    SM / MID{' '}
                    <span className="text-red-500" data-id="element-833" data-mp="2w7bhky6pz7br">{pkg.smMid}</span>
                    <span className="text-zinc-700 mx-2" data-id="element-834" data-mp="uhdzdl8rhfwu">·</span>
                    SUV / CROSS{' '}
                    <span className="text-red-500" data-id="element-835" data-mp="z8cistp1qu4b">{pkg.suvCross}</span>
                  </p>
                </div>
                <p className="text-gray-400 mb-3 leading-relaxed" data-id="element-836" data-mp="kjptq09manzw">{pkg.note}</p>
                <p className="text-gray-300 leading-relaxed" data-id="element-837" data-mp="phrkz8h75tmg">
                  <span className="font-bold text-white" data-id="element-838" data-mp="pfx0twdr25jf">Includes: </span>
                  {pkg.includes.join(', ')}.
                </p>
              </div>)}
          </div>

          <h3 className="text-lg font-black uppercase text-white mt-8 mb-4" data-id="element-839" data-mp="1i5draoht7cd0">
            Extra Services
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-840" data-mp="139689ri50gzb">
            {EXTRAS_TABLE.map(extra => <div key={extra.name} className="flex items-center justify-between gap-4 bg-zinc-900/40 border border-zinc-800 rounded-xl px-5 py-4" data-id="element-841" data-mp="186y0kmmp2541">
                <dt className="text-gray-200 font-bold" data-id="element-842" data-mp="3syhuhxbzmosw">{extra.name}</dt>
                <dd className="text-red-500 font-black whitespace-nowrap" data-id="element-843" data-mp="3swdkcxnhwtk6">
                  {extra.price}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* FAQ categories */}
      {FAQ_CATEGORIES.map(category => <section key={category.id} id={category.id} className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby={`${category.id}-heading`} data-id="element-844" data-mp="2k0rx4e69tzyl">
          <div className="max-w-4xl mx-auto" data-id="element-845" data-mp="1san4xtc9s8mt">
            <h2 id={`${category.id}-heading`} className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-846" data-mp="1sbf0x59s48dm">
              {category.title}
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed max-w-3xl" data-id="element-847" data-mp="213gx2rctf2i3">
              {category.intro}
            </p>
            <dl className="space-y-4" data-id="element-848" data-mp="1wb5xxqpwfkql">
              {category.faqs.map(faq => <div key={faq.question} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-849" data-mp="18mmm517n4ptr">
                  <dt className="text-white font-bold text-lg" data-id="element-850" data-mp="14latsnxdtqhv">
                    {faq.question}
                  </dt>
                  <dd className="mt-3 text-gray-300 text-base leading-relaxed" data-id="element-851" data-mp="1tgljxmao0hq8">
                    {faq.answer}
                  </dd>
                </div>)}
            </dl>
          </div>
        </section>)}

      {/* Where to learn more */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="learn-more" data-id="element-852" data-mp="22imjiptdffv2">
        <div className="max-w-4xl mx-auto" data-id="element-853" data-mp="27d8ks05qns1f">
          <h2 id="learn-more" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-854" data-mp="2cfhfp2269466">
            Learn More About Each <span className="text-red-600" data-id="element-855" data-mp="2hc7uzn65jvqj">Service</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-856" data-mp="2m495b3jrfcdx">
            <FaqLink to="/services/mobile-detailing" label="Mobile Detailing" data-id="element-857" data-mp="2q62zcszg9i54" />
            <FaqLink to="/services/full-car-detail" label="Full Car Detail" data-id="element-858" data-mp="2q876rkwk4za5" />
            <FaqLink to="/services/interior-detailing" label="Interior Detailing" data-id="element-859" data-mp="2v6qsbk78o91z" />
            <FaqLink to="/services/exterior-detailing" label="Exterior Detailing" data-id="element-860" data-mp="305207pdz49hk" />
            <FaqLink to="/services/hybrid-ceramic-spray" label="Hybrid Ceramic Spray" data-id="element-861" data-mp="3a8h1w5f54nzf" />
            <FaqLink to="/services/headlight-restoration" label="Headlight Restoration" data-id="element-862" data-mp="358kya7acuyke" />
            <FaqLink to="/services/engine-bay-detailing" label="Engine Bay Detailing" data-id="element-863" data-mp="309ri273ootgj" />
          </div>

          <h3 className="text-lg font-black uppercase text-white mb-4" data-id="element-864" data-mp="2w8xvbh1z9kp8">
            Service Areas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-865" data-mp="3syyi5aisdr3w">
            {CITY_PAGE_AREAS.map(city => <FaqLink key={city} to={`/mobile-detailing-${city.toLowerCase().replace(/\s+/g, '-')}`} label={city} data-id="element-866" data-mp="3nzc2yvjee0sg" />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" data-id="element-867" data-mp="3f7jwsx1epdto">
        <div className="max-w-4xl mx-auto text-center" data-id="element-868" data-mp="3f5h8chfwjsr2">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-869" data-mp="fklqlqbego99">
            Still Have a Question?
          </h2>
          <p className="text-gray-400 mb-6" data-id="element-870" data-mp="aqzdjx1bqicx">
            Call or text Mike directly and get a straight answer.
          </p>
          <a href="tel:8183819905" className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 text-white font-black uppercase tracking-wide rounded-lg hover:bg-red-500 transition-colors duration-150" aria-label="Call Mike's Pro Detail at (818) 381-9905" data-id="element-871" data-mp="ghts7in6gosy">
            <Phone className="w-5 h-5" aria-hidden="true" data-id="element-872" data-mp="pgxbonq9b67z" />
            (818) 381-9905
          </a>
        </div>
      </section>

      <Contact data-id="element-873" data-mp="zgfs4u6yk9va" />
    </main>;
}
function FaqLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-colors duration-150" data-id="element-874" data-mp="1d7kwg9l9tqby">
      {label} &rarr;
    </Link>;
}